// "Your turn" on a phone: a drawer that rises from the bottom of the chat. On a
// phone the panel over the post box was too narrow and fought with the
// keyboard, so the chat keeps one folded line and pressing it raises this
// (the person decided so, 2026-10-04). The panel hands it what it shows
// (agent-chat/turn/drawer-port.ts); this only holds it.
//
// Promise:
// - It lies over the chat's column (under the header), drawn into it, never
//   fixed (in a fixed box iOS did not redraw the caret where a field moved).
//   The column is laid over the visible part of the screen while the keyboard
//   is up (keyboard.ts), so the drawer stays above the keyboard.
// - Its height is its content's; a long one reaches up to 8 px under the
//   header and scrolls inside. CSS decides it (nothing is measured).
// - The buttons that answer sit in a row at its bottom, out of the scroll.
//   While the person writes in a field that takes several lines (the keyboard
//   is up), that row sinks away and its room goes to the writing (the
//   drawer's top stays where it was); it comes back when the keyboard closes
//   or the focus leaves. While any field inside has the keyboard, the home
//   bar's room at its bottom is dropped (it was a gap above the keyboard).
// - When the keyboard makes its scroll shorter, the field being written stays
//   in sight: the whole field when it fits, else where the caret is.
// - While it is up, the Dock's row (the way back, the post box, the round
//   button) is stowed: it sinks away, keeping its room, so the chat does not
//   move.
// - It closes by swiping the grabber or the tag row down, by its ×, Escape,
//   the scrim above it, and the browser's or the OS's back (history.ts: no
//   page move). Closing with the focus inside gives it back to the folded line.
// - Moves take 200 ms (none with reduced motion). Down, it stays mounted,
//   hidden and inert: what is being written and files going up stay.
import { useRouter } from "@tanstack/react-router";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { TurnDrawerProps } from "../agent-chat/turn/drawer-port";
import { isImeComposing } from "../ui/ime";
import { usePhoneState } from "./context";
import {
  isOverlayEntry,
  leaveOverlayEntry,
  overlayKind,
  pushOverlayEntry,
} from "./history";
import { useSwipeDown } from "./swipe-down";
import { useTypingIn } from "./typing";

/** Pulled down slowly, it closes past this share of its height (as the old web). */
const DRAG_CLOSE_RATIO = 0.25;
/** Kept between a field with the focus and the edges of the scroll. */
const KEEP_MARGIN_PX = 12;

/** A field that takes several lines (the answer's), not a one-line input of a form. */
function isWritingField(box: HTMLElement | null, el: Element): boolean {
  if (!(el instanceof HTMLElement) || !box?.contains(el)) return false;
  const multi = el.tagName === "TEXTAREA" || el.isContentEditable;
  return multi && el.closest("[data-turn-field]") !== null;
}

/** Where the caret is in a field that writes rich text (its line when empty). */
function caretRect(): DOMRect | null {
  const selection = document.getSelection();
  if (!selection || selection.rangeCount === 0) return null;
  const range = selection.getRangeAt(0);
  const rects = range.getClientRects();
  const rect = rects[rects.length - 1] ?? range.getBoundingClientRect();
  if (rect.top !== 0 || rect.bottom !== 0) return rect;
  const node = range.startContainer;
  const line = node instanceof Element ? node : node.parentElement;
  return line?.getBoundingClientRect() ?? null;
}

/**
 * When the scroll got shorter (the keyboard rose), the field with the focus
 * is brought back into it (only this scroll moves). The whole field (with its
 * label) when it fits; a field taller than the scroll shows where the caret
 * is, not its head (a long answer was shown from its first line while the
 * person wrote at its end). Both kinds of field: a text area and one that
 * writes rich text.
 */
function keepFocusInView(area: HTMLElement): void {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement) || !area.contains(active)) return;
  const rich = active.isContentEditable;
  if (!rich && active.tagName !== "TEXTAREA" && active.tagName !== "INPUT")
    return;
  const frame = area.getBoundingClientRect();
  const whole = (
    active.closest("label, [data-turn-field]") ?? active
  ).getBoundingClientRect();
  const fits = whole.height + 2 * KEEP_MARGIN_PX <= frame.height;
  const caret = rich ? caretRect() : null;
  const target = fits || !caret ? whole : caret;
  const above = target.top - KEEP_MARGIN_PX - frame.top;
  const below = target.bottom + KEEP_MARGIN_PX - frame.bottom;
  if (above < 0) area.scrollTop += above;
  else if (below > 0) area.scrollTop += Math.min(below, above);
}

export function TurnDrawer({
  open,
  label,
  host,
  onClose,
  head,
  children,
  onFoot,
  itemKey,
  opener,
  onOpen,
}: TurnDrawerProps) {
  const router = useRouter();
  const stow = usePhoneState()?.stow;
  const box = useRef<HTMLDivElement | null>(null);
  const area = useRef<HTMLDivElement | null>(null);
  const close = useRef(onClose);
  close.current = onClose;
  const writing = useTypingIn(
    (active) => isWritingField(box.current, active),
    open,
  );
  // Any field inside has the keyboard (a form's one-line input too).
  const keyboard = useTypingIn(
    (active) => box.current?.contains(active) === true,
    open,
  );
  const swipe = useSwipeDown(
    () => close.current(),
    () => box.current,
    DRAG_CLOSE_RATIO,
  );
  // While writing, the drawer keeps the height it had with the bottom row (its
  // top does not move as the row sinks; the scroll takes the row's room). Read
  // once when writing starts, before the row leaves.
  const [held, setHeld] = useState<number | null>(null);
  if (!writing && held !== null) setHeld(null);
  const sunk = writing && held !== null;
  // frontend-development: not a fetch; keeps the height when writing starts.
  useLayoutEffect(() => {
    if (writing && held === null && box.current)
      setHeld(box.current.offsetHeight);
  }, [writing, held]);

  // frontend-development: not a fetch; asks the Dock to step aside while up.
  useLayoutEffect(() => (open && stow ? stow() : undefined), [open, stow]);

  // The browser's back closes it; closing by other means leaves the entry.
  // frontend-development: not a fetch; the history entry of an overlay.
  const kind = `turn:${itemKey}`;
  const kindRef = useRef(kind);
  kindRef.current = kind;
  useEffect(() => {
    if (!open) return;
    const history = router.history;
    pushOverlayEntry(history, kindRef.current);
    let up = true;
    const off = history.subscribe(() => {
      if (up && !isOverlayEntry(history)) close.current();
    });
    return () => {
      up = false;
      off();
      leaveOverlayEntry(history);
    };
  }, [open, router]);

  // Back from a page a link inside it opened, onto its entry: up again, as it
  // was left (it came back as the folded line).
  const reopen = useRef(onOpen);
  reopen.current = onOpen;
  // frontend-development: not a fetch; raises it again on its own history entry.
  // biome-ignore lint/correctness/useExhaustiveDependencies: once per item drawn, not on every close
  useEffect(() => {
    if (!open && overlayKind(router.history) === kind) reopen.current?.();
  }, [router, kind]);

  // Up: the focus goes to the drawer (the line pressed lies under it), never
  // into a field (no keyboard by itself). Down with the focus inside: back to
  // the folded line, before it is hidden.
  // frontend-development: not a fetch; where the focus goes on open and close.
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    if (open) {
      if (!el.contains(document.activeElement))
        el.focus({ preventScroll: true });
    } else if (el.contains(document.activeElement))
      opener.current?.focus({ preventScroll: true });
  }, [open, opener]);

  // The keyboard shrank the scroll: the field with the focus stays in sight.
  // frontend-development: not a fetch; follows the scroll's height.
  // biome-ignore lint/correctness/useExhaustiveDependencies: the scroll is drawn anew for each item (`key`), so it is observed anew
  useLayoutEffect(() => {
    const el = area.current;
    if (!open || !el || typeof ResizeObserver === "undefined") return;
    let height = el.clientHeight;
    const ro = new ResizeObserver(() => {
      const next = el.clientHeight;
      if (next < height) keepFocusInView(el);
      height = next;
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [open, itemKey]);

  const dragging = swipe.dragY > 0;
  return createPortal(
    <>
      {/* The chat shows dimmed above it, so it reads as a surface laid over;
          pressing there closes it. */}
      <button
        type="button"
        tabIndex={-1}
        aria-hidden="true"
        data-turn-drawer-scrim=""
        onClick={() => close.current()}
        className={`absolute inset-0 z-40 cursor-default bg-black/30 transition-opacity duration-200 ease-out motion-reduce:transition-none ${open ? "opacity-100" : "pointer-events-none opacity-0"}`}
      />
      <div
        ref={box}
        role="dialog"
        aria-label={label}
        aria-hidden={!open || undefined}
        inert={!open || undefined}
        tabIndex={-1}
        data-turn-drawer=""
        onKeyDown={(e) => {
          if (
            e.key !== "Escape" ||
            e.defaultPrevented ||
            isImeComposing(e.nativeEvent)
          )
            return;
          e.preventDefault();
          close.current();
        }}
        style={{
          // The same `translate` as the classes: released, it moves on from
          // where the finger left it (no jump back to the top first).
          ...(dragging ? { translate: `0 ${swipe.dragY}px` } : {}),
          ...(sunk ? { minHeight: `min(${held}px, 100% - 8px)` } : {}),
        }}
        // Over the chat and the Dock's row. Hidden once down (visibility
        // moves with it going down; going up it is visible at once, or the
        // focus could not move in at the start of the move).
        // The home bar's room only while the keyboard is down: above the
        // keyboard it was a gap.
        className={`absolute inset-x-0 bottom-0 z-40 flex max-h-[calc(100%-8px-var(--phone-top,0px))] flex-col overflow-hidden rounded-t-[20px] border-t border-turn-box bg-surface text-fg shadow-[var(--shadow-sheet)] outline-none duration-200 ease-out ${keyboard ? "" : "has-[[data-turn-drawer-foot]:empty]:pb-[env(safe-area-inset-bottom)]"} motion-reduce:transition-none ${dragging ? "transition-none" : open ? "transition-[translate]" : "transition-[translate,visibility]"} ${open ? "translate-y-0" : "invisible translate-y-full"}`}
      >
        {/* The grabber and the tag row: swiping down here closes (the
            vertical move is not the browser's). */}
        <div
          className="flex-none touch-none select-none px-4 pb-2"
          {...swipe.handlers}
        >
          <div aria-hidden="true" className="flex justify-center pt-2.5 pb-2">
            <span className="h-1 w-10 rounded-full bg-turn-box" />
          </div>
          {head}
        </div>
        <div
          key={itemKey}
          ref={area}
          className="flex min-h-0 flex-auto flex-col overflow-y-auto overscroll-contain px-4"
        >
          <div className="flex flex-none flex-col">{children}</div>
          {/* The room at the end is a box, not the scroll's padding: WebKit
              keeps what sticks to the bottom (the answer field's + row) above
              the padding, and the field showed through under it. */}
          <div aria-hidden="true" className="h-4 flex-none" />
        </div>
        {/* The row of the buttons that answer (folded when there is none).
            While writing it leaves the flow at once (the scroll takes its
            room) and only its look sinks away. */}
        <div
          ref={onFoot}
          data-turn-drawer-foot=""
          inert={sunk || undefined}
          className={`flex-none border-t border-border-soft bg-surface px-4 pt-2.5 ${keyboard ? "pb-2.5" : "pb-[max(10px,env(safe-area-inset-bottom))]"} transition-[translate,opacity,visibility] duration-200 ease-out empty:hidden motion-reduce:transition-none ${sunk ? "pointer-events-none invisible absolute inset-x-0 bottom-0 translate-y-full opacity-0" : ""}`}
        />
      </div>
    </>,
    host,
  );
}
